import { DbRef, ShipmentType, SupplyShipmentStatusName, GenericStatusIndicator, GenericStatusInfoTitle, DraftWaypointStatus, DraftWaypointSupplyStrategy, SupplyTransferStatusName, Role, cancelationReasonFormatted, supplyConditionFormatted, ProcedureStatus, GenericStatusInfoIconName, SampleStatus, procedureStatusFormatted, failureReasonFormatted } from '@caresend/types';
import { getPath } from './path.js';
import { hasStatus } from './status.js';
import { formatLocalDate, formatTimeZoneDateSimple } from '../formatters/date.js';
import { formatFullName } from '../formatters/user.js';

/* eslint-disable max-len */
/**
 * ```
 * `${DbRef.SUPPLY_ITEMS}/${id}/${childPath}`
 * ```
 */

function getSupplyItemPath(id, childPath) {
  return getPath(`${DbRef.SUPPLY_ITEMS}/${id}`, childPath);
}
/**
 * ```
 * `${DbRef.SUPPLY_TRANSFERS}/${id}/${childPath}`
 * ```
 */

function getSupplyTransferPath(id, childPath) {
  return getPath(`${DbRef.SUPPLY_TRANSFERS}/${id}`, childPath);
}
/**
 * ```
 * `${DbRef.SHIPMENTS}/${id}/${childPath}`
 * ```
 */

function getShipmentPath(id, childPath) {
  return getPath(`${DbRef.SHIPMENTS}/${id}`, childPath);
}
/**
 * ```
 * `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`
 * ```
 */

const getFedExTrackingURL = trackingNumber => `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`;
const getFedExTrackingNumberFromURL = url => {
  const trackingNumber = url.split('trknbr=')[1];
  return trackingNumber;
};
const getGenericStatusInfoForShipment = shipment => {
  const isSupply = shipment.type === ShipmentType.SUPPLY;
  const statusDate = formatLocalDate(shipment.status.timestamp, 'M/D/YYYY, h:mm a');
  /**
   * Shorter subtitles used for non-supply shipments.
   * - `SupplyShipment` - Since 3/13/2021, 3:00 pm
   * - `Shipment` - 3/13/2021, 3:00 pm
   */

  const getSubtitle = (supplyPrefixString = '') => isSupply ? `${supplyPrefixString} ${statusDate}` : statusDate;

  switch (shipment.status.status) {
    case SupplyShipmentStatusName.PENDING_LABEL:
    case SupplyShipmentStatusName.READY_TO_SHIP:
      return {
        color: GenericStatusIndicator.PURPLE,
        title: GenericStatusInfoTitle.READY_FOR_SHIPMENT,
        subtitle: getSubtitle('Since')
      };

    case SupplyShipmentStatusName.IN_TRANSIT:
      return Object.assign({
        color: GenericStatusIndicator.YELLOW,
        title: GenericStatusInfoTitle.SHIPPED,
        subtitle: getSubtitle('Since')
      }, isSupply && {
        callBackButtonLabel: 'Push to delivered'
      });

    case SupplyShipmentStatusName.DELIVERED:
      return {
        color: GenericStatusIndicator.GREEN,
        title: GenericStatusInfoTitle.DELIVERED,
        subtitle: getSubtitle('On')
      };

    case SupplyShipmentStatusName.DRAFT:
      return {
        color: GenericStatusIndicator.YELLOW,
        title: GenericStatusInfoTitle.SHOPIFY_ORDER_PENDING_FINALIZATION
      };
  }
};
/**
 * Returns status info for a given set of draft waypoint, supply transfer
 * and supply shipments.
 */

const getGenericStatusInfoForDraftWaypointSupplies = (draftWaypoint, supplyTransfer, supplyShipments) => {
  const defaultStatus = {
    color: GenericStatusIndicator.GREY,
    title: GenericStatusInfoTitle.DEFAULT,
    subtitle: ''
  };

  if (!supplyTransfer) {
    if (draftWaypoint.draftOrderID) {
      return [{
        color: GenericStatusIndicator.GREY,
        title: GenericStatusInfoTitle.DRAFT,
        subtitle: 'Order is draft'
      }];
    }

    if (hasStatus(draftWaypoint, DraftWaypointStatus.CANCELED)) {
      return [{
        color: GenericStatusIndicator.RED,
        title: GenericStatusInfoTitle.CANCELED
      }];
    }

    switch (draftWaypoint.supplyStrategy) {
      case DraftWaypointSupplyStrategy.CUSTOM_SHIPMENT:
        return [{
          color: GenericStatusIndicator.ORANGE,
          title: GenericStatusInfoTitle.PENDING,
          subtitle: 'Generating supply transfer'
        }];

      case DraftWaypointSupplyStrategy.KIT_SHIPMENT:
        return [{
          color: GenericStatusIndicator.GREEN,
          title: GenericStatusInfoTitle.CONFIRMED,
          subtitle: 'Kit shipment'
        }];

      case DraftWaypointSupplyStrategy.OFFLINE_SUPPLIES:
        return [{
          color: GenericStatusIndicator.GREEN,
          title: GenericStatusInfoTitle.CONFIRMED,
          subtitle: 'Offline supplies'
        }];

      case undefined:
        return [{
          color: GenericStatusIndicator.RED,
          title: GenericStatusInfoTitle.ACTION_NEEDED,
          subtitle: 'Needs supply strategy'
        }];
    }
  }

  switch (supplyTransfer === null || supplyTransfer === void 0 ? void 0 : supplyTransfer.status.status) {
    case SupplyTransferStatusName.CREATED:
      return [{
        color: GenericStatusIndicator.RED,
        title: GenericStatusInfoTitle.ACTION_NEEDED,
        subtitle: 'Create shipping label'
      }];

    case SupplyTransferStatusName.CANCELED:
      return [{
        color: GenericStatusIndicator.RED,
        title: GenericStatusInfoTitle.CANCELED
      }];

    case SupplyTransferStatusName.ASSIGNED:
      return [{
        color: GenericStatusIndicator.ORANGE,
        title: GenericStatusInfoTitle.READY_FOR_PACKING,
        subtitle: `Since ${formatLocalDate(supplyTransfer.status.timestamp, 'M/D/YYYY, h:mm a')}`
      }];

    case SupplyTransferStatusName.IN_PROGRESS:
      return [{
        color: GenericStatusIndicator.BLUE,
        title: GenericStatusInfoTitle.IN_PROGRESS,
        subtitle: `Since ${formatLocalDate(supplyTransfer.status.timestamp, 'M/D/YYYY, h:mm a')}`
      }];

    case SupplyTransferStatusName.PACKED:
      if (!(supplyShipments === null || supplyShipments === void 0 ? void 0 : supplyShipments.length)) return [defaultStatus];
      return supplyShipments.map(shipment => getGenericStatusInfoForShipment(shipment));
  }
};
/**
 * Resulting array will have the most recent item first
 */

const sortItemHistoryByTimestamp = history => {
  if (!history) return [];
  const historyArray = Object.values(history !== null && history !== void 0 ? history : {});
  return historyArray.sort((a, b) => {
    if (!a.timestamp.timestamp || !b.timestamp.timestamp) return 0;
    return a.timestamp.timestamp > b.timestamp.timestamp ? -1 : 1;
  });
};

const getDraftWaypointTransitInfo = (draftWaypoint, waypoint) => {
  if ((draftWaypoint === null || draftWaypoint === void 0 ? void 0 : draftWaypoint.status) === DraftWaypointStatus.IN_ITINERARY && draftWaypoint.waypointID) {
    return waypoint === null || waypoint === void 0 ? void 0 : waypoint.transitInfo;
  }
};

const getDisplayUserName = user => {
  if (!user) return '';
  const name = (user === null || user === void 0 ? void 0 : user.role) === Role.SUPERADMIN ? 'CareSend admin' : (user === null || user === void 0 ? void 0 : user.role) === Role.NURSE ? formatFullName(user, false, true) : formatFullName(user, false, false);
  return `By ${name} `;
};
const formatProcedureDateSubtitle = (procedure, timestamp) => {
  var _a, _b;

  if (!timestamp) return '';
  const date = {
    timestamp,
    timeZone: (_b = (_a = procedure.location) === null || _a === void 0 ? void 0 : _a.timeZone) !== null && _b !== void 0 ? _b : 'UTC'
  };
  return `on ${formatTimeZoneDateSimple(date, 'MMM D, YYYY')}`;
};
const getFormattedCancellationReason = procedure => {
  var _a;

  if (!((_a = procedure.procedureCanceled) === null || _a === void 0 ? void 0 : _a.reason)) return;
  return cancelationReasonFormatted[procedure.procedureCanceled.reason];
};
const getProcedureCanceledSupplyStatus = procedure => {
  var _a;

  const supplyCondition = (_a = procedure.procedureCanceled) === null || _a === void 0 ? void 0 : _a.supplyCondition;
  if (!supplyCondition) return;
  return supplyConditionFormatted[supplyCondition];
};
const getProcedureFailedSupplyStatus = procedure => {
  var _a;

  const supplyCondition = (_a = procedure.failed) === null || _a === void 0 ? void 0 : _a.supplyCondition;
  if (!supplyCondition) return;
  return supplyConditionFormatted[supplyCondition];
};
const getProcedureStatusInfo = ({
  canceledByUser,
  draftWaypoint,
  failedByUser,
  hasReattempts,
  isDashboardUser,
  isDraft,
  nurse,
  openCancelModal,
  partnerName,
  procedure,
  shipment,
  waypoint
}) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;

  const getVisitUrl = () => {
    if (isDashboardUser) return `/supply-transfers/${procedure.draftWaypointID}`;
    const formatedPartnerOfficeName = partnerName ? `/${partnerName}` : '';
    return `${formatedPartnerOfficeName}/visit/${procedure.draftWaypointID}`;
  };

  const cancellationReason = getFormattedCancellationReason(procedure);
  const cancellationReasonDisplay = `${cancellationReason ? `| ${cancellationReason}` : ''}`;
  const procedureCanceledSupplyStatus = getProcedureCanceledSupplyStatus(procedure);
  const procedureCanceledSupplyStatusDisplay = `${procedureCanceledSupplyStatus ? `, ${procedureCanceledSupplyStatus.toLowerCase()}` : ''}`;
  const reAttemptDisplay = hasReattempts ? ' | Has reattempts' : '';

  const getCanceledProcedureStatusInfo = () => {
    var _a;

    return {
      title: `
          ${procedureStatusFormatted[GenericStatusInfoTitle.CANCELED]}
          ${cancellationReasonDisplay}${procedureCanceledSupplyStatusDisplay}${reAttemptDisplay}
        `,
      color: GenericStatusIndicator.RED,
      iconName: GenericStatusInfoIconName.CANCELED,
      subtitle: `
          ${getDisplayUserName(canceledByUser)}${formatProcedureDateSubtitle(procedure, (_a = procedure.procedureCanceled) === null || _a === void 0 ? void 0 : _a.timestamp)}
        `,
      callBackButtonLabel: 'Cancelation details',
      callBackButton: openCancelModal,
      callBackButtonSecondaryLabel: 'Visit',
      callBackButtonSecondary: () => window.open(getVisitUrl(), '_blank')
    };
  };

  const procedureFailedSupplyStatus = getProcedureFailedSupplyStatus(procedure);
  const procedureFailedSupplyStatusDisplay = `${procedureFailedSupplyStatus ? `, ${procedureFailedSupplyStatus.toLowerCase()}` : ''}`;

  const getFailedProcedureStatusInfo = () => {
    var _a, _b;

    const status = procedureStatusFormatted[GenericStatusInfoTitle.FAILED];
    const reason = ((_a = procedure.failed) === null || _a === void 0 ? void 0 : _a.reason) ? `: ${failureReasonFormatted[procedure.failed.reason]}` : '';
    const supplyStatus = procedureFailedSupplyStatusDisplay;
    return {
      title: `${status}${reason}${supplyStatus}`,
      color: GenericStatusIndicator.RED,
      iconName: GenericStatusInfoIconName.FAILED,
      subtitle: `
          ${getDisplayUserName(failedByUser)}${formatProcedureDateSubtitle(procedure, (_b = procedure.failed) === null || _b === void 0 ? void 0 : _b.timestamp)}
        `,
      callBackButtonLabel: 'Failure details',
      callBackButton: openCancelModal,
      callBackButtonSecondaryLabel: 'Visit',
      callBackButtonSecondary: () => window.open(getVisitUrl(), '_blank')
    };
  };

  if (isDraft) {
    if (procedure.status === ProcedureStatus.CANCELED) {
      return getCanceledProcedureStatusInfo();
    }

    return {
      color: GenericStatusIndicator.GREY,
      iconName: GenericStatusInfoIconName.CIRCLE,
      subtitle: `${formatProcedureDateSubtitle(procedure, procedure.creationTimestamp)}`,
      title: GenericStatusInfoTitle.DRAFT
    };
  }

  if (procedure.labResults) {
    const firstResultTimestamp = (_c = (_b = Object.values((_a = procedure.labResults) !== null && _a !== void 0 ? _a : {})[0]) === null || _b === void 0 ? void 0 : _b.file) === null || _c === void 0 ? void 0 : _c.timestamp;
    return {
      title: GenericStatusInfoTitle.RESULTED,
      color: GenericStatusIndicator.GREEN,
      subtitle: formatProcedureDateSubtitle(procedure, firstResultTimestamp)
    };
  }

  switch (procedure.status) {
    case ProcedureStatus.CREATED:
      return {
        iconName: GenericStatusInfoIconName.DELIVERY,
        title: procedureStatusFormatted[GenericStatusInfoTitle.PENDING],
        subtitle: `${formatProcedureDateSubtitle(procedure, procedure.creationTimestamp)}`,
        color: GenericStatusIndicator.GREY,
        callBackButtonLabel: 'Visit',
        callBackButton: () => window.open(getVisitUrl(), '_blank')
      };

    case ProcedureStatus.IN_PROGRESS:
      {
        const arrivalTimestamp = (_e = (_d = getDraftWaypointTransitInfo(draftWaypoint, waypoint)) === null || _d === void 0 ? void 0 : _d.arrivalTime) === null || _e === void 0 ? void 0 : _e.timestamp;
        return {
          iconName: GenericStatusInfoIconName.IN_PROGRESS,
          title: procedureStatusFormatted[GenericStatusInfoTitle.IN_PROGRESS],
          color: GenericStatusIndicator.ORANGE,
          subtitle: `${getDisplayUserName(nurse)}${formatProcedureDateSubtitle(procedure, arrivalTimestamp)}`,
          callBackButtonLabel: 'Visit',
          callBackButton: () => window.open(getVisitUrl(), '_blank')
        };
      }

    case ProcedureStatus.COMPLETED:
      {
        const atLeastOneSampleIsDropedOff = Object.values((_f = procedure.samples) !== null && _f !== void 0 ? _f : {}).some(sample => sample.status === SampleStatus.COMPLETED);
        const firstSampleCollectionTimestamp = (_h = Object.values((_g = procedure.samples) !== null && _g !== void 0 ? _g : {})[0]) === null || _h === void 0 ? void 0 : _h.collectionTimestamp;

        if (!atLeastOneSampleIsDropedOff) {
          return {
            iconName: GenericStatusInfoIconName.COLLECTED,
            title: procedureStatusFormatted[GenericStatusInfoTitle.COLLECTED],
            color: GenericStatusIndicator.BLUE,
            subtitle: `${getDisplayUserName(nurse)}${formatProcedureDateSubtitle(procedure, firstSampleCollectionTimestamp)}`,
            callBackButtonLabel: 'Visit',
            callBackButton: () => window.open(getVisitUrl(), '_blank')
          };
        }

        const trackingUrl = shipment === null || shipment === void 0 ? void 0 : shipment.trackingURL;

        if ((shipment === null || shipment === void 0 ? void 0 : shipment.status.status) === SupplyShipmentStatusName.DELIVERED) {
          return {
            iconName: GenericStatusInfoIconName.DELIVERED,
            title: procedureStatusFormatted[GenericStatusInfoTitle.DELIVERED],
            subtitle: `${getDisplayUserName(nurse)}${formatProcedureDateSubtitle(procedure, firstSampleCollectionTimestamp)}`,
            color: GenericStatusIndicator.GREEN,
            callBackButtonSecondaryLabel: trackingUrl ? 'Track shipment' : undefined,
            callBackButtonSecondary: () => window.open(trackingUrl, '_blank'),
            callBackButtonLabel: 'Visit',
            callBackButton: () => window.open(getVisitUrl(), '_blank')
          };
        }

        const firstSampleDropOffTimestamp = (_k = Object.values((_j = procedure.samples) !== null && _j !== void 0 ? _j : {}).find(sample => !!sample.dropOffTimestamp)) === null || _k === void 0 ? void 0 : _k.dropOffTimestamp;
        const submittedGenericStatus = trackingUrl ? GenericStatusInfoTitle.SUBMITTED_WITH_TRACKING : GenericStatusInfoTitle.SUBMITTED;
        return {
          iconName: GenericStatusInfoIconName.SUBMITTED,
          title: procedureStatusFormatted[submittedGenericStatus],
          color: GenericStatusIndicator.PURPLE,
          subtitle: `${getDisplayUserName(nurse)}${formatProcedureDateSubtitle(procedure, firstSampleDropOffTimestamp)}`,
          callBackButtonSecondaryLabel: trackingUrl ? 'Track shipment' : undefined,
          callBackButtonSecondary: () => window.open(trackingUrl, '_blank'),
          callBackButtonLabel: 'Visit',
          callBackButton: () => window.open(getVisitUrl(), '_blank')
        };
      }

    case ProcedureStatus.CANCELED:
      return getCanceledProcedureStatusInfo();

    case ProcedureStatus.FAILED:
      return getFailedProcedureStatusInfo();

    default:
      return {
        iconName: GenericStatusInfoIconName.DELIVERY,
        title: GenericStatusInfoTitle.PENDING,
        color: GenericStatusIndicator.ORANGE
      };
  }
};
const getPackingDeadline = (patientShipmentInfo, orderDescriptor, isMailInVisit) => {
  if (orderDescriptor === 'draft') {
    return patientShipmentInfo.packingDeadlineDraft;
  }

  if (isMailInVisit && patientShipmentInfo.packingDeadlineFreezing) {
    return patientShipmentInfo.packingDeadlineFreezing;
  }

  return patientShipmentInfo.packingDeadlineFull;
};
/**
 * Get the required supplies from the procedure supply instructions
 * This is used by the supply filter to determine if a visit can be fulfilled
 * based on the procedures' supply requirements
 */

const getRequiredSuppliesFromInstructions = supplyInstructions => {
  const allRequiredSupplies = new Set();
  Object.entries(supplyInstructions).forEach(([_, supplyInstructionsForProcedure]) => {
    const instructionsByPlaceGroup = supplyInstructionsForProcedure === null || supplyInstructionsForProcedure === void 0 ? void 0 : supplyInstructionsForProcedure.supplyInstructionsByPlaceGroup;
    if (!instructionsByPlaceGroup) return;
    Object.entries(instructionsByPlaceGroup).forEach(([__, supplyInstructionConfig]) => {
      var _a, _b;

      const supplyInstructionsConfiguration = (_a = supplyInstructionConfig.supplyInstructionsByDeliveryType.walkIn) !== null && _a !== void 0 ? _a : supplyInstructionConfig.supplyInstructionsByDeliveryType.mailIn;
      if (!supplyInstructionsConfiguration) return;
      const supplyRequirements = Object.keys((_b = supplyInstructionsConfiguration === null || supplyInstructionsConfiguration === void 0 ? void 0 : supplyInstructionsConfiguration.supplyInstructions) !== null && _b !== void 0 ? _b : {});
      supplyRequirements.forEach(supplyID => allRequiredSupplies.add(supplyID));
    });
  });
  return Array.from(allRequiredSupplies);
};

export { formatProcedureDateSubtitle, getDisplayUserName, getFedExTrackingNumberFromURL, getFedExTrackingURL, getFormattedCancellationReason, getGenericStatusInfoForDraftWaypointSupplies, getGenericStatusInfoForShipment, getPackingDeadline, getProcedureCanceledSupplyStatus, getProcedureFailedSupplyStatus, getProcedureStatusInfo, getRequiredSuppliesFromInstructions, getShipmentPath, getSupplyItemPath, getSupplyTransferPath, sortItemHistoryByTimestamp };
